import {
  ChangeDetectionStrategy, Component, ViewChild,
  ChangeDetectorRef, NgZone, OnInit, OnChanges, OnDestroy,
} from '@angular/core';
import * as _ from 'lodash';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';

import { BrandService } from 'shared/brand/brand.service';
import { ProcessService } from 'shared/process/process.service';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { SearchPageService } from 'shared/search-page/search-page.service';
import { SavedSearchesService } from 'shared/saved-searches/saved-searches.service';
import { UserService } from 'shared/user/user.service';

import { SearchPageComponent } from 'shared/search-page/search-page.component';
import { SearchPageSimpleHeaderComponent } from 'shared/search-page/search-page-simple-header/search-page-simple-header.component';
import { ListManagerService } from 'shared/list-manager/list-manager.service';
import { ErrorModalComponent } from 'shared/modal/error-modal/error-modal.component';

import { AppRoutingService } from '../app-routing.service';

import { PermissionsService } from 'shared/permissions/permissions.service';
import { ReturnRouteService } from 'shared/return-route/return-route.service';
import { SearchModel } from 'shared/search/search.model';
import { PreferencesService } from 'shared/preferences/preferences.service';
import { ReportsService } from 'shared/reports/reports.service';
import { FinalModalComponent } from 'shared/modal/final-modal/final-modal.component';

import { columns } from './search/search-results-columns';
import { Config } from 'shared/config/env.config';
import { ReportsSearchService } from './search/reports-search.service';
import { ConfigureReportModalComponent } from './components/configure-report-modal/configure-report-modal.component';
import { actions } from './reports-actions';
import {filter, takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'sd-reports',
  templateUrl: 'reports.component.html',
  styleUrls: ['reports.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportsComponent extends SearchPageComponent implements OnInit, OnDestroy {

  contentSelector: string = '#reports-content';
  rowsHover: boolean = true;
  searchInputPlaceholder: string = 'Search for a report by name...';
  incompleteReports: Array<string> = [];
  intervalId: any = null;
  timezone: any;
  actions: any = actions;
  showSidedrawer: boolean = true; // use this field to show sidedrawer
  ghostValue: string = 'export-manager';
  helpLink: string = Config.DEFAULT_SUPPORT_URL;

  @ViewChild(ConfigureReportModalComponent, {static: false})
  confReportModal: ConfigureReportModalComponent | void;

  @ViewChild(FinalModalComponent, {static: false})
  finalModal: FinalModalComponent | void;

  @ViewChild(SearchPageSimpleHeaderComponent, {static: false})
  declare headerEl: SearchPageSimpleHeaderComponent | void;

  private routerChangeSub: Subscription;

  constructor(
    public searchService: ReportsSearchService,
    public reportsService: ReportsService,
    protected brandService: BrandService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected pageService: SearchPageService,
    protected processService: ProcessService,
    protected scrollToService: ScrollToService,
    protected appRoutingService: AppRoutingService,
    protected permissionsService: PermissionsService,
    protected ngZone: NgZone,
    protected ref: ChangeDetectorRef,
    protected returnRouteService: ReturnRouteService,
    protected location: Location,
    protected preferencesService: PreferencesService,
    protected savedSearchesService: SavedSearchesService,
    protected userService: UserService,
  ) {
    super(
      searchService, brandService, pageService, processService, scrollToService,
      appRoutingService, null, permissionsService, ngZone, ref, route, location,
      savedSearchesService, router, userService,
    );
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get selectedRowsSize(): number {
    // if selected all items return total records length
    if (this.allSelected) {
      return this.totalRecords;
    }
    // if there is no sort by status return all selected rows length
    return this.selectedRows.length;
  }

  // implemented
  get storedSearchModel(): SearchModel | null {
    if (_.has(this, 'user.id')) {
      return JSON.parse(localStorage.getItem(`reportsSearchModel.${this.brandService.getBrand()}.${this.user.id}`));
    } else {
      return null;
    }
  }

  get searchInputValue(): string {
    // handle it in the components
    // to get search value from header search input
    return this.headerEl && this.headerEl.searchInputValue;
  }

  // implemented
  get headerColor(): string {
    return 'rum';
  }

  get page(): string {
    return this.route.snapshot.params['page'];
  }

  get isLeftTabActive(): boolean {
    return this.page === 'download';
  }

  get isRightTabActive(): boolean {
    return this.page === 'view';
  }

  get allowSearchRun(): boolean {
    return this.page === 'download';
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    this.searchResultsColumns = _.cloneDeep(columns);

    this.searchRanSubj
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((results: any) => {

      this.incompleteReports = [];

      if (results.data && results.data.length) {
        const incompleteReports = _.map(_.filter(results.data, (item: any) => {
          return !item.download.completed;
        }), 'id');

        this.incompleteReports = [...this.incompleteReports, ...incompleteReports];

        if (this.incompleteReports.length) {
          this.startCompletedRequests();
        }
      }
    });

    // get timezone
    this.preferencesService.getPreferences()
    .subscribe((preferences: any) => {
      this.timezone = preferences.timezone;
    });

    super.ngOnInit();
  }

  ngOnDestroy() {
    if (this.intervalId) {
      this.intervalId = clearInterval(this.intervalId);
    }
    super.ngOnDestroy();
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  // implemented
  storeSearchModel(searchModel: SearchModel) {
    if (_.has(this, 'user.id')) {
      localStorage.setItem(`reportsSearchModel.${this.brandService.getBrand()}.${this.user.id}`, JSON.stringify(searchModel));
    }
  }

  setSearchResultsOffset(value?: number) {
    this.searchResultsOffset = value + 340; // add reports-info height
  }

  initSubscriptions() {
    super.initSubscriptions();
    this.routerChangeSub = this.subscribeRouterChange();
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onSearchCall() {
    if (this.searchModel) {
      this.searchModel.currentPage = 1;
      this.searchModel.searchValue = this.searchForm && this.searchForm.searchTermValue;
      this._searchModel.next(this.searchModel);
    }

    this.searchService.toggleForm(false);
  }

  onCancelClick() {
    this.searchService.toggleForm(false);
  }

  onConfigureReportClick() {
    if (this.confReportModal instanceof ConfigureReportModalComponent) {
      this.confReportModal.show();
    }
  }

  onConfigureReportFinished() {
    if (this.confReportModal instanceof ConfigureReportModalComponent) {
      this.confReportModal.hide();
    }
    if (this.finalModal instanceof FinalModalComponent) {
      this.finalModal.show();
    }
    this.runSearch(this.searchModel);
  }

  onLeftBtnClick() {
    if (this.finalModal instanceof FinalModalComponent) {
      this.finalModal.hide();
    }
    if (this.confReportModal instanceof ConfigureReportModalComponent) {
      this.confReportModal.show();
    }
  }

  onRightBtnClick() {
    if (this.finalModal instanceof FinalModalComponent) {
      this.finalModal.hide();
    }
  }

  onFinalModalHidden() {
    if (this.searchModel instanceof SearchModel) {
      this._searchModel.next(this.searchModel);
    }
  }

  onLeftTabClick() {
    this.router.navigate(['/reports/download']);
  }

  onRightTabClick() {
    this.router.navigate(['/reports/view']);
  }

  onPageActionSelect(event: any) {
    if (event.action === 'configure') {
      if (this.confReportModal instanceof ConfigureReportModalComponent) {
        this.confReportModal.show();
      }
    }
  }


  /*******************************************
   * COMPONENT PROTECTED METHODS
   *******************************************/

   protected addDataToColumns(): void {
     const columnsWithData: Array<any> = [];
     _.each(this.searchResultsColumns, (column: any) => {
        const newColumn = _.cloneDeep(column);
        const found: any = _.find(columns, {'col': column.col} );
        if (found && found.data) {
          newColumn.data = found.data;
        }
        columnsWithData.push(newColumn);
     });
     this.searchResultsColumns = columnsWithData;
   }

   /*******************************************
    * COMPONENT PRIVATE METHODS
    *******************************************/

   private startCompletedRequests() {
     if (this.intervalId) {
       if (!this.incompleteReports.length) {
         this.intervalId = clearInterval(this.intervalId);
       }
       return;
    }

     this.intervalId = setInterval(() => {
       this.reportsService.getCompleted(
         this.brandService.getBrand(),
         this.incompleteReports,
       )
       .pipe(takeWhile(() => this.isPageActive && !!this.incompleteReports.length))
       .subscribe((completed) => {
         // @ts-ignore
           _.forEach( _.filter(this.searchService.searchResults, {download: {completed: false}}), (result: any) => {

           const completedResult = _.find(completed, (c) => {
             return c.id === result.id && c.completed === true;
           });

           if (completedResult) {
             result.status = completedResult.status;
             result.download.completed = completedResult.completed;
             result.download.has_error = completedResult.has_error;
             _.pull(this.incompleteReports, result.id);
           }
         });
         if (!this.incompleteReports.length && this.intervalId) {
           this.intervalId = clearInterval(this.intervalId);
         }
         this.ref.markForCheck();
       });
     }, 10000);
   }

   private subscribeRouterChange(): Subscription {
     return this.router.events
     .pipe(filter(event => event instanceof NavigationEnd))
     .subscribe((event: NavigationEnd) => {
       this.handleRouterChange(event);
     });
   }

   private handleRouterChange(event?: NavigationEnd): void {
     this.showEmptyResultsState = false;
     if (this.allowSearchRun) {
       this.searchService.searchResults = [];
       this.searchService.selectAllChecked = [];
       this.searchService.selectedItems = [];
       this.searchService.allSelected = false;
       if (this.searchModel instanceof SearchModel) {
         this._searchModel.next(this.searchModel);
       }
     }
   }
}
